import { useEffect, useState } from 'react'
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react'
import { prefersReducedMotion, useInView } from '@/components/motion/useInView'
import { cn } from '@/lib/utils'

/**
 * Looping product vignettes for the landing feature blocks — animated,
 * self-contained demos of the actual product behavior (the role screen-
 * recording GIFs play on sites like Recurly, but crisp, themeable, and
 * reduced-motion aware). Each loop only runs while scrolled into view.
 */

function useVignettePhase<T extends string>(
  timeline: { phase: T; ms: number }[],
  active: boolean,
): T {
  const [index, setIndex] = useState(0)
  useEffect(() => {
    if (!active) return
    const step = timeline[index]!
    const timer = window.setTimeout(
      () => setIndex((current) => (current + 1) % timeline.length),
      step.ms,
    )
    return () => window.clearTimeout(timer)
  }, [active, index, timeline])
  return timeline[index]!.phase
}

/* ————— 1. SSO login swap (secure user management) ————— */

const SSO_EMAIL = 'priya@acme.com'
type SsoPhase = 'typing' | 'resolving' | 'sso'

export function SsoLoginVignette() {
  const { ref, inView } = useInView<HTMLDivElement>()
  const reduced = prefersReducedMotion()
  const active = inView && !reduced

  const [typed, setTyped] = useState(reduced ? SSO_EMAIL : '')
  const [phase, setPhase] = useState<SsoPhase>(reduced ? 'sso' : 'typing')

  useEffect(() => {
    if (!active) return
    let timer: number
    if (phase === 'typing') {
      if (typed.length < SSO_EMAIL.length) {
        timer = window.setTimeout(() => setTyped(SSO_EMAIL.slice(0, typed.length + 1)), 65)
      } else {
        timer = window.setTimeout(() => setPhase('resolving'), 500)
      }
    } else if (phase === 'resolving') {
      timer = window.setTimeout(() => setPhase('sso'), 900)
    } else {
      timer = window.setTimeout(() => {
        setTyped('')
        setPhase('typing')
      }, 3_400)
    }
    return () => window.clearTimeout(timer)
  }, [active, phase, typed])

  return (
    <div
      ref={ref}
      data-vignette="sso"
      className="w-full max-w-sm rounded-2xl border bg-surface-raised p-5 shadow-card"
      aria-label="Demo: login form detecting enterprise SSO from the email domain"
    >
      <div className="mb-4 flex items-center gap-2.5">
        <span className="flex size-7 items-center justify-center rounded-lg bg-brand">
          <KeyRound className="size-3.5 text-accent-on-dark" aria-hidden />
        </span>
        <p className="text-[13px] font-semibold text-content">Sign in to Vault</p>
        <span className="ml-auto rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-[9px] font-semibold text-content-muted">
          hosted login
        </span>
      </div>

      <p className="mb-1.5 text-[11px] font-medium text-content-secondary">Work email</p>
      <div className="mb-3 flex h-9 items-center rounded-md border border-input bg-surface-raised px-2.5 font-mono text-xs text-content">
        {typed}
        <span
          className={cn(
            'ml-px inline-block h-3.5 w-px bg-content',
            active && 'animate-caret-blink',
          )}
          aria-hidden
        />
      </div>

      <div className="relative h-[76px]">
        {/* Password variant */}
        <div
          className={cn(
            'absolute inset-0 transition-all duration-300',
            phase === 'sso' ? 'pointer-events-none translate-y-1 opacity-0' : 'opacity-100',
          )}
        >
          <p className="mb-1.5 text-[11px] font-medium text-content-secondary">Password</p>
          <div className="flex h-9 items-center rounded-md border border-input bg-surface-raised px-2.5 font-mono text-xs tracking-widest text-content-muted">
            {phase === 'resolving' ? (
              <span className="flex items-center gap-2 font-sans tracking-normal text-content-muted">
                <Loader2 className="size-3 animate-spin" aria-hidden /> Checking sign-in options…
              </span>
            ) : (
              '••••••••'
            )}
          </div>
        </div>
        {/* SSO variant */}
        <div
          className={cn(
            'absolute inset-0 flex flex-col justify-end transition-all duration-300',
            phase === 'sso' ? 'opacity-100' : 'pointer-events-none -translate-y-1 opacity-0',
          )}
        >
          <div className="mb-2 flex items-center gap-1.5 text-[10.5px] font-semibold text-accent-strong">
            <ShieldCheck className="size-3" aria-hidden /> SSO detected for @acme.com
          </div>
          <div className="flex h-9 items-center justify-center gap-2 rounded-md bg-brand text-xs font-semibold text-white">
            <span className="flex size-4 items-center justify-center rounded bg-white/15 font-mono text-[8px] font-bold">
              OK
            </span>
            Continue with Okta
          </div>
        </div>
      </div>

      <p className="mt-3 border-t border-line-subtle pt-2.5 font-mono text-[10px] text-content-muted">
        GET /auth/sso/resolve?email=… <span className="text-accent-strong">200</span>
      </p>
    </div>
  )
}

/* ————— 2. Subscription lifecycle (trial → paid) ————— */

type SubPhase = 'trial' | 'converted'

export function SubscriptionLifecycleVignette() {
  const { ref, inView } = useInView<HTMLDivElement>()
  const reduced = prefersReducedMotion()
  const active = inView && !reduced
  const phase = useVignettePhase<SubPhase>(
    [
      { phase: 'trial', ms: 3_000 },
      { phase: 'converted', ms: 3_400 },
    ],
    active,
  )
  const shown: SubPhase = reduced ? 'converted' : phase
  const converted = shown === 'converted'

  return (
    <div
      ref={ref}
      data-vignette="subscription"
      className="w-full max-w-sm rounded-2xl border bg-surface-raised p-5 shadow-card"
      aria-label="Demo: a trial converting to a paid subscription"
    >
      <div className="mb-3.5 flex items-center justify-between">
        <p className="font-mono text-[11px] font-semibold text-content">sub_71bqz</p>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 font-mono text-[9.5px] font-bold transition-colors duration-500',
            converted
              ? 'bg-status-active-bg text-status-active-fg'
              : 'bg-status-trial-bg text-status-trial-fg',
          )}
        >
          {converted ? 'ACTIVE' : 'TRIAL'}
        </span>
      </div>

      <div className="mb-2 h-[7px] overflow-hidden rounded bg-surface-sunken">
        <div
          className={cn(
            'h-full rounded transition-all ease-linear',
            converted ? 'bg-status-active duration-500' : 'bg-status-trial duration-[2800ms]',
          )}
          style={{ width: active ? (converted ? '100%' : '92%') : '92%' }}
        />
      </div>
      <div className="mb-4 flex justify-between font-mono text-[10px] text-content-muted">
        <span>started 18 Aug</span>
        <span>{converted ? 'renews 24 Sep' : 'converts 24 Aug'}</span>
      </div>

      <div className="flex flex-col gap-1.5 font-mono text-[10.5px]">
        <div className="flex items-center gap-2 rounded-md bg-surface-page px-2.5 py-1.5 text-content-body">
          <span className="size-1.5 rounded-full bg-status-trial" aria-hidden />
          trial.started
          <span className="ml-auto text-content-muted">18 Aug</span>
        </div>
        <div
          className={cn(
            'flex items-center gap-2 rounded-md bg-surface-page px-2.5 py-1.5 text-content-body transition-all duration-400',
            converted ? 'animate-row-in' : 'opacity-30',
          )}
        >
          <span className="size-1.5 rounded-full bg-accent" aria-hidden />
          trial.converted
          <span className="ml-auto text-content-muted">24 Aug</span>
        </div>
        <div
          className={cn(
            'flex items-center gap-2 rounded-md bg-surface-page px-2.5 py-1.5 text-content-body transition-all duration-400',
            converted ? 'animate-row-in delay-200' : 'opacity-30',
          )}
        >
          <span className="size-1.5 rounded-full bg-accent" aria-hidden />
          invoice.paid
          <span className="ml-auto font-semibold text-status-active-fg">$49.00</span>
        </div>
      </div>

      <p className="mt-3 border-t border-line-subtle pt-2.5 font-mono text-[10px] text-content-muted">
        Idempotency-Key sent · charge exactly once
      </p>
    </div>
  )
}

/* ————— 3. Live webhook delivery feed (operations) ————— */

interface WebhookEvent {
  type: string
  status: number
  latency: string
  note?: string
}

const WEBHOOK_EVENTS: WebhookEvent[] = [
  { type: 'subscription.activated', status: 200, latency: '88ms' },
  { type: 'invoice.paid', status: 200, latency: '96ms' },
  { type: 'payment.failed', status: 504, latency: '10,004ms', note: 'retry 2/5' },
  { type: 'payment.failed', status: 200, latency: '112ms', note: 'retried' },
  { type: 'user.registered', status: 200, latency: '74ms' },
  { type: 'entitlement.updated', status: 200, latency: '81ms' },
]

export function WebhookFeedVignette() {
  const { ref, inView } = useInView<HTMLDivElement>()
  const reduced = prefersReducedMotion()
  const active = inView && !reduced
  const [count, setCount] = useState(reduced ? 4 : 2)

  useEffect(() => {
    if (!active) return
    const timer = window.setInterval(() => setCount((current) => current + 1), 1_400)
    return () => window.clearInterval(timer)
  }, [active])

  const visible = Array.from({ length: 4 }, (_, offset) => {
    const index = count - offset
    if (index < 0) return null
    return { event: WEBHOOK_EVENTS[index % WEBHOOK_EVENTS.length]!, key: index }
  }).filter((entry): entry is { event: WebhookEvent; key: number } => entry !== null)

  return (
    <div
      ref={ref}
      data-vignette="webhooks"
      className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.04] p-5"
      aria-label="Demo: live webhook delivery log with retries"
    >
      <div className="mb-3.5 flex items-center gap-2">
        <span className="size-1.5 rounded-full bg-accent animate-status-ping" aria-hidden />
        <p className="text-xs font-semibold text-white">Webhook deliveries</p>
        <span className="ml-auto font-mono text-[10px] text-sidebar-muted">live</span>
      </div>

      <div className="flex min-h-[152px] flex-col gap-1.5" aria-live="off">
        {visible.map(({ event, key }) => (
          <div
            key={key}
            className="animate-row-in flex items-center gap-2 rounded-md bg-white/[0.05] px-2.5 py-1.5 font-mono text-[10.5px] text-[#8FA0B8]"
          >
            <span className="truncate text-console-fg">{event.type}</span>
            {event.note ? <span className="text-sidebar-muted">{event.note}</span> : null}
            <span
              className={cn(
                'ml-auto font-semibold',
                event.status < 400 ? 'text-accent-on-dark' : 'text-[#FB7185]',
              )}
            >
              {event.status}
            </span>
            <span className="w-16 text-right">{event.latency}</span>
          </div>
        ))}
      </div>

      <p className="mt-3 border-t border-white/10 pt-2.5 font-mono text-[10px] text-sidebar-muted">
        Retry ladder 0s · 30s · 2m · 8m · 32m → dead-letter
      </p>
    </div>
  )
}

/* ————— 4. White-label portal preview (seat bar fills in view) ————— */

export function PortalPreviewVignette() {
  const { ref, inView } = useInView<HTMLDivElement>()

  return (
    <div
      ref={ref}
      data-vignette="portal"
      className="w-full max-w-sm overflow-hidden rounded-2xl border border-[#C9E4D5] bg-[#F0FDF4]"
      aria-label="Demo: a white-labeled customer portal"
    >
      <div className="flex items-center gap-2 border-b border-[#D7EFE0] bg-surface-raised px-3.5 py-2">
        <span className="flex gap-1" aria-hidden>
          <span className="size-2 rounded-full bg-line-strong" />
          <span className="size-2 rounded-full bg-line-strong" />
        </span>
        <span className="rounded border border-[#E2EFE7] bg-[#F7FAF8] px-2 py-0.5 font-mono text-[10px] text-[#3F6B54]">
          billing.northwind.dev/vault
        </span>
      </div>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <span className="flex size-6 items-center justify-center rounded-md bg-[#166534] font-mono text-[9px] font-bold text-white">
            V
          </span>
          <span className="text-[13px] font-semibold text-[#052E1B]">Vault</span>
          <span className="ml-auto text-[10.5px] text-[#3F6B54]">priya@brightpath.dev</span>
        </div>
        <div className="rounded-xl border border-[#C9E4D5] bg-surface-raised p-3.5">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[13px] font-semibold text-[#052E1B]">Vault Pro</span>
            <span className="rounded bg-status-trial-bg px-1.5 py-0.5 font-mono text-[9px] font-bold text-status-trial-fg">
              TRIAL · 3 DAYS
            </span>
          </div>
          <p className="text-[11.5px] text-[#3F6B54]">$49.00/mo after 26 Aug · card on file</p>
        </div>
        <div>
          <div className="mb-1.5 flex justify-between font-mono text-[10px] text-[#7A9C89]">
            <span>seats used</span>
            <span>4 / 10</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded bg-[#E2EFE7]">
            <div
              className="h-full rounded bg-[#166534] transition-all duration-1000 ease-out"
              style={{ width: inView ? '40%' : '0%' }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
